/**
 * AmenitiesPanel — view↔edit orchestrator for the property-detail Amenities tab
 * (SCRUM-141 / DS-010-5).
 *
 * Owns the local `mode` ('view' | 'edit') and reads the property's amenities to
 * drive the read view + its fetch-state branches. In view mode it renders
 * `AmenitiesView`; the Edit affordance is wrapped in the shipped
 * `<RequirePermission slug="property.amenity.write">` seam (fallback={null}) so
 * a viewer without write permission never sees it (codex MEDIUM-4). In edit
 * mode it renders `AmenitiesEditor`, which self-fetches the catalog + current
 * assignment.
 *
 * Read-fetch branches degrade gracefully rather than crash: forbidden →
 * forbidden-state; not-found → not-found message; loading → loading indicator;
 * error → error message.
 */

import * as React from 'react';
import { RequirePermission } from '../../../../shared/auth/RequirePermission.js';
import { usePropertyAmenities } from '../../api/use-property-amenities.js';
import { AmenitiesView } from './AmenitiesView.js';
import { AmenitiesEditor } from './AmenitiesEditor.js';

export interface AmenitiesPanelProps {
  readonly propertyId: string;
}

export function AmenitiesPanel({
  propertyId,
}: AmenitiesPanelProps): React.ReactElement {
  const [mode, setMode] = React.useState<'view' | 'edit'>('view');
  const amenities = usePropertyAmenities(propertyId);

  if (mode === 'edit') {
    return (
      <AmenitiesEditor
        propertyId={propertyId}
        onDone={() => setMode('view')}
      />
    );
  }

  if (amenities.status === 'loading') {
    return (
      <div role="status" className="py-8 text-center text-sm text-[color:var(--fg-secondary)]">
        Loading amenities…
      </div>
    );
  }
  if (amenities.status === 'forbidden') {
    return (
      <div className="py-8 text-center text-sm text-[color:var(--fg-secondary)]">
        You do not have permission to view amenities for this property.
      </div>
    );
  }
  if (amenities.status === 'not-found') {
    return (
      <div className="py-8 text-center text-sm text-[color:var(--fg-secondary)]">
        This property was not found. It may have been removed.
      </div>
    );
  }
  if (amenities.status === 'error') {
    return (
      <div className="py-8 text-center text-sm text-[color:var(--fg-secondary)]">
        Something went wrong while loading amenities.
      </div>
    );
  }

  // Ready: render the read view; the Edit affordance is permission-gated.
  return (
    <RequirePermission
      slug="property.amenity.write"
      fallback={<AmenitiesView groups={amenities.data} />}
    >
      <AmenitiesView groups={amenities.data} onEdit={() => setMode('edit')} />
    </RequirePermission>
  );
}
